import {
    type BoardCardFieldColumns,
    BoardDisplayPopover,
} from "@design-system/Board/BoardDisplayPopover";
import { AdjustmentsIcon } from "@icons";
import { TaskColumn } from "@common/types/views/tasksView";
import { TaskGroupBy } from "@common/types/task";
import type { TaskGroupingContext } from "../taskListCapabilities";
import {
    parseTaskBoardDisplayChange,
    type TaskBoardDisplayChange,
    type TaskBoardDisplayState,
} from "./taskBoardCache";
import {
    isTaskBoardLaneByAvailable,
    TASK_BOARD_LANE_OPTIONS,
} from "./taskBoardFields";
import type { TaskBoardLaneBy } from "./taskBoardLanes";
import { getTaskBoardSortLabel, TASK_BOARD_SORTS } from "./taskBoardSort";

interface TaskBoardDisplayButtonProps {
    display: TaskBoardDisplayState;
    /** The board's column grouping, which no swimlane can repeat. */
    groupBy: TaskGroupBy;
    /** The swimlanes the board draws (`getTaskBoardLaneBy`), shown as the Swimlanes value. */
    laneBy: TaskBoardLaneBy | null;
    /** The same context the board's lanes come from, so both offer the same lanes. */
    groupingContext: TaskGroupingContext;
    onDisplayChange: (change: TaskBoardDisplayChange) => void;
    /** List's columns, as List's column settings rows for the ones a card can show. */
    cardFields: BoardCardFieldColumns;
}

/**
 * Board's display options: swimlanes, sort and direction, card fields and empty
 * columns. Offers the groupings the header picker offers, plus Entity. Card
 * fields are List's columns, so showing, hiding or reordering one does the
 * same in List.
 */
export const TaskBoardDisplayButton = ({
    display,
    groupBy,
    laneBy,
    groupingContext,
    onDisplayChange,
    cardFields,
}: Readonly<TaskBoardDisplayButtonProps>) => {
    const isAvailable = (option: TaskBoardLaneBy) =>
        isTaskBoardLaneByAvailable({ ...groupingContext, laneBy: option });

    const sortOptions = TASK_BOARD_SORTS.filter(
        (sort) =>
            sort !== TaskColumn.CLIENT ||
            groupingContext.capabilities.isCrossWorkspace,
    ).map((sort) => ({ value: sort, label: getTaskBoardSortLabel(sort) }));

    return (
        <BoardDisplayPopover
            value={{ ...display, groupBy: laneBy, columnBy: groupBy }}
            onChange={(change) =>
                onDisplayChange(parseTaskBoardDisplayChange(change))
            }
            groupByOptions={TASK_BOARD_LANE_OPTIONS.filter(({ value }) =>
                isAvailable(value),
            )}
            groupByLabel="Swimlanes"
            sortOptions={sortOptions}
            sortDirection={display.sortDirection}
            onSortDirectionChange={(sortDirection) =>
                onDisplayChange({ sortDirection })
            }
            cardFields={cardFields}
            triggerIcon={<AdjustmentsIcon className="icon-color-gray-900" />}
            triggerButtonProps={{ variant: "primary", size: "md" }}
        />
    );
};
